/**
 * Convert Measurement Units generator (6.PAFR.2.7).
 *
 * Converts between customary/metric units using exact ratio reasoning with the
 * shared CONVERSIONS factors. Conversions are set up so the answer is a tidy
 * rational: multiplying a whole count of the larger unit by a whole factor, or
 * dividing a value that is a whole multiple of the factor. The answer is
 * derived from the same factor shown in the step, so it always matches.
 *
 * Families:
 *   F1: length    (feet<->inches, yards<->feet, miles<->feet)
 *   F2: weight    (pounds<->ounces)
 *   F3: capacity  (gallons<->quarts, quarts<->pints, pints<->cups)
 *   F4: time      (hours<->minutes, minutes<->seconds, days<->hours)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { CONVERSIONS, convert } from "../engine/ratio";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "convert-units" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

/** A conversion where 1 `big` = `factor` `small` (e.g. 1 foot = 12 inches). */
interface UnitPair {
  readonly big: string;
  readonly small: string;
  readonly factor: Rational;
}

const PAIRS: Record<Family, readonly UnitPair[]> = {
  F1: [
    { big: "foot", small: "inch", factor: CONVERSIONS.inchesPerFoot },
    { big: "yard", small: "foot", factor: CONVERSIONS.feetPerYard },
    { big: "mile", small: "foot", factor: CONVERSIONS.feetPerMile },
  ],
  F2: [{ big: "pound", small: "ounce", factor: CONVERSIONS.ozPerPound }],
  F3: [
    { big: "gallon", small: "quart", factor: CONVERSIONS.quartsPerGallon },
    { big: "quart", small: "pint", factor: CONVERSIONS.pintsPerQuart },
    { big: "pint", small: "cup", factor: CONVERSIONS.cupsPerPint },
  ],
  F4: [
    { big: "hour", small: "minute", factor: CONVERSIONS.minutesPerHour },
    { big: "minute", small: "second", factor: CONVERSIONS.secondsPerMinute },
    { big: "day", small: "hour", factor: CONVERSIONS.hoursPerDay },
  ],
};

const DIFF: Record<Difficulty, readonly Family[]> = {
  easy: ["F1", "F4"],
  standard: ["F1", "F2", "F3", "F4"],
  hard: ["F1", "F2", "F3", "F4"],
  challenge: ["F1", "F2", "F3", "F4"],
};

/** Pluralize a unit name for display (simple English rules suffice here). */
function plural(unit: string, count: Rational): string {
  if (count.equals(rat(1))) return unit;
  if (unit === "foot") return "feet";
  if (unit === "inch") return "inches";
  return `${unit}s`;
}

export class ConvertUnitsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const families = DIFF[ctx.difficulty];
      const family = rng.pick(families);
      const pair = rng.pick(PAIRS[family]);

      // Direction: big->small multiplies by factor; small->big divides.
      const bigToSmall = rng.bool(0.5);

      let value: Rational;
      let answer: Rational;
      let fromUnit: string;
      let toUnit: string;

      if (bigToSmall) {
        // Whole count of the larger unit times a whole factor -> tidy integer.
        value = rat(rng.int(2, 12));
        answer = convert(value, pair.factor, "multiply");
        fromUnit = pair.big;
        toUnit = pair.small;
      } else {
        // Build the value as a whole multiple of the factor so division is tidy.
        const multiples = rng.int(2, 9);
        value = pair.factor.mul(rat(multiples));
        answer = convert(value, pair.factor, "divide");
        fromUnit = pair.small;
        toUnit = pair.big;
      }

      const promptText = `How many ${plural(toUnit, answer)} are in ${value.toString()} ${plural(fromUnit, value)}?`;
      const promptLatex = `${value.toLatex()}\\text{ ${plural(fromUnit, value)}} = \\;?\\text{ ${plural(toUnit, answer)}}`;

      const steps = [
        {
          description: bigToSmall
            ? `1 ${pair.big} = ${pair.factor.toString()} ${plural(pair.small, pair.factor)}. Multiply by the factor.`
            : `${pair.factor.toString()} ${plural(pair.small, pair.factor)} = 1 ${pair.big}. Divide by the factor.`,
          latex: bigToSmall
            ? `${value.toLatex()} \\times ${pair.factor.toLatex()} = ${answer.toLatex()}`
            : `${value.toLatex()} \\div ${pair.factor.toLatex()} = ${answer.toLatex()}`,
        },
      ];

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "Decide whether you are going to a smaller unit (multiply) or a larger unit (divide)." },
          { level: 2, text: "Use the conversion factor between the two units, then multiply or divide." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const convertUnits = new ConvertUnitsGenerator();
